/**
 * PatientReportsModal.js
 *
 * Two-level view:
 *  1. List view  — thumbnails / icons, filename, date.  Data fetched lazily on open.
 *  2. Detail view — large preview, meta, editable description, print button.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from './api';

const BASE = process.env.REACT_APP_URL;

/* ── useSecureBlob ────────────────────────────────────────────────────────────
 * Fetches a patient file via the authenticated API endpoint and returns a
 * revokable blob URL.  Falls back to null while loading or on error.
 * The blob URL is automatically revoked on unmount.
 */
function useSecureBlob(apiPath) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [unsafe, setUnsafe] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!apiPath) { setUnsafe(false); return; }
    let cancelled = false;
    api.get(apiPath, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        if (res.headers?.['x-file-unsafe'] === '1') setUnsafe(true);
        else setUnsafe(false);
        const u = URL.createObjectURL(res.data);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => setBlobUrl(null));
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [apiPath]);

  return { blobUrl, unsafe };
}

/* ── helpers ─────────────────────────────────────────────────────────────── */
const IMAGE_EXTS = ['jpg','jpeg','png','gif','webp','bmp','tiff'];

function isImage(filename) {
  return IMAGE_EXTS.includes((filename || '').split('.').pop().toLowerCase());
}

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Strip the leading timestamp prefix added by backend (e.g. "1717000000000-report.pdf" → "report.pdf")
function displayName(filename) {
  return filename.replace(/^\d{13}-/, '');
}

// Derive file type label
function fileType(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (IMAGE_EXTS.includes(ext)) return ext.toUpperCase();
  if (['doc','docx'].includes(ext)) return 'Word';
  if (['xls','xlsx'].includes(ext)) return 'Excel';
  return ext.toUpperCase() || 'File';
}

function fileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (IMAGE_EXTS.includes(ext)) return null; // thumbnail shown instead
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  return '📎';
}

/* ── overlay backdrop ─────────────────────────────────────────────────────── */
const Backdrop = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      position: 'fixed', inset: 0, zIndex: 8000,
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(2px)',
    }}
  />
);

/* ── thumbnail card in list view ─────────────────────────────────────────── */
function ReportCard({ file, patientCard, onClick }) {
  // Fetch thumbnail via authenticated API (files are encrypted on disk)
  const apiPath = isImage(file.filename) ? `/patient-files/${patientCard}/reports/${file.filename}` : null;
  const { blobUrl, unsafe } = useSecureBlob(apiPath);

  return (
    <div
      onClick={() => onClick(file)}
      style={{
        cursor: 'pointer',
        borderRadius: 10,
        border: unsafe ? '3px solid #dc2626' : '1px solid #e5e7eb',
        background: unsafe ? '#fef2f2' : '#fff',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, transform 0.12s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = ''; }}
    >
      {unsafe && (
        <div style={{ position: 'absolute', top: 4, left: 4, zIndex: 2, background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
          Not encrypted — re-upload
        </div>
      )}
      {/* Thumbnail / icon area */}
      <div style={{
        height: 110,
        background: '#f8fafc',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderBottom: '1px solid #f0f0f0',
        overflow: 'hidden',
      }}>
        {isImage(file.filename) && blobUrl ? (
          <img
            src={blobUrl}
            alt={file.filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            fontSize: 36,
            display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            width: '100%', height: '100%',
          }}>
            {isImage(file.filename) && !blobUrl ? '⏳' : (fileIcon(file.filename) || '📎')}
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName(file.filename)}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
          {fileType(file.filename)} · {formatBytes(file.size)}
        </div>
        <div style={{ fontSize: 10, color: '#c4c9d4', marginTop: 2 }}>
          {formatDate(file.uploadedAt)}
        </div>
        {file.description && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            "{file.description}"
          </div>
        )}
      </div>
    </div>
  );
}

/* ── detail popup ─────────────────────────────────────────────────────────── */
function ReportDetail({ file, patientCard, onBack, onDelete }) {
  const [desc, setDesc]       = useState(file.description || '');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch file blob via authenticated API (handles decryption server-side)
  const apiPath = `/patient-files/${patientCard}/reports/${file.filename}`;
  const { blobUrl, unsafe } = useSecureBlob(apiPath);

  const isPdf = file.filename.toLowerCase().endsWith('.pdf');
  const isImg = isImage(file.filename);

  async function saveDesc() {
    setSaving(true);
    try {
      await api.patch(`${BASE}/patient-files/${patientCard}/reports/${file.filename}/desc`, { description: desc });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    setSaving(false);
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${displayName(file.filename)}"? Cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`${BASE}/patient-files/${patientCard}/reports/${file.filename}`);
      onDelete(file.filename);
    } catch (_) {
      alert('Delete failed');
      setDeleting(false);
    }
  }

  function handlePrint() {
    if (!blobUrl) return alert('File is still loading, please wait.');
    const win = window.open(blobUrl, '_blank');
    if (win) setTimeout(() => win.print(), 800);
  }

  function handleDownload() {
    if (!blobUrl) return alert('File is still loading, please wait.');
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = displayName(file.filename);
    a.click();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Unsafe file warning banner */}
      {unsafe && (
        <div style={{ background: '#dc2626', color: '#fff', padding: '6px 16px', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
          This file is not encrypted on storage — please re-upload it
        </div>
      )}
      {/* Detail header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 18px',
        borderBottom: '1px solid #f0f0f0',
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid #e5e7eb', background: '#f9fafb',
            color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName(file.filename)}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {fileType(file.filename)} · {formatBytes(file.size)} · {formatDate(file.uploadedAt)}
            {file.uploadedBy && <span> · by {file.uploadedBy}</span>}
          </div>
        </div>
        <button
          onClick={handlePrint}
          style={{
            padding: '7px 16px', borderRadius: 7,
            border: '1px solid #2563eb', background: '#eff6ff',
            color: '#1d4ed8', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          🖨 Print
        </button>
        <button
          onClick={handleDownload}
          disabled={!blobUrl}
          style={{
            padding: '7px 14px', borderRadius: 7,
            border: '1px solid #d1d5db', background: '#fff',
            color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ⬇ Download
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          style={{
            padding: '7px 12px', borderRadius: 7,
            border: '1px solid #fca5a5', background: '#fff5f5',
            color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {deleting ? '…' : '🗑 Delete'}
        </button>
      </div>

      {/* Preview area */}
      <div style={{ flex: 1, overflow: 'auto', background: '#1a1c2e', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
        {!blobUrl ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</div>
        ) : isPdf ? (
          <iframe
            src={blobUrl}
            title={file.filename}
            style={{ width: '100%', height: '100%', border: 'none', minHeight: 400 }}
          />
        ) : isImg ? (
          <img
            src={blobUrl}
            alt={file.filename}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
          />
        ) : (
          <div style={{ color: '#9ca3af', fontSize: 48, textAlign: 'center', padding: 40 }}>
            {fileIcon(file.filename) || '📎'}
            <div style={{ fontSize: 14, marginTop: 12 }}>No preview available</div>
            <button
              onClick={handleDownload}
              style={{ display: 'inline-block', marginTop: 12, color: '#60a5fa', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer' }}>
              Download file ↓
            </button>
          </div>
        )}
      </div>

      {/* Description + meta */}
      <div style={{ padding: '12px 18px', borderTop: '1px solid #f0f0f0', background: '#fafafa', flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Description / Notes</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="Add a note about this report (e.g. MRI left knee, March 2025)…"
            style={{
              flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb',
              borderRadius: 7, fontSize: 13, color: '#1f2937', background: '#fff',
              outline: 'none',
            }}
          />
          <button
            onClick={saveDesc}
            disabled={saving}
            style={{
              padding: '8px 16px', borderRadius: 7,
              border: 'none', background: saved ? '#22c55e' : '#2563eb',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {saved ? '✓ Saved' : saving ? '…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── main modal ───────────────────────────────────────────────────────────── */
export default function PatientReportsModal({ patientCard, patientName, onClose }) {
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);  // file in detail view

  // serverBase no longer needed — files are served via authenticated API endpoint

  useEffect(() => {
    if (!patientCard) return;
    api.get(`${BASE}/patient-files/${patientCard}/reports`)
      .then(res => setFiles(res.data || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [patientCard]);

  function handleDelete(filename) {
    setFiles(prev => prev.filter(f => f.filename !== filename));
    setSelected(null);
  }

  // Reflect description edits back to list
  function handleDescChange(filename, description) {
    setFiles(prev => prev.map(f => f.filename === filename ? { ...f, description } : f));
  }

  return (
    <>
      <Backdrop onClick={onClose} />
      <div style={{
        position: 'fixed',
        top: '5vh', left: '50%', transform: 'translateX(-50%)',
        width: 'min(92vw, 860px)',
        height: '88vh',
        zIndex: 8001,
        background: '#fff',
        borderRadius: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 20px',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16 }}>🧾</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
              Lab Reports & Scans
            </div>
            {patientName && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>
                {patientName}
              </div>
            )}
          </div>
          {selected && (
            <button
              onClick={() => setSelected(null)}
              style={{
                padding: '5px 12px', borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)',
                color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ← All Reports
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {selected ? (
            /* ── Detail view ── */
            <ReportDetail
              file={selected}
              patientCard={patientCard}
              onBack={() => setSelected(null)}
              onDelete={handleDelete}
            />
          ) : (
            /* ── List view ── */
            <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
              {loading ? (
                <div style={{ color: '#9ca3af', fontSize: 14, padding: 40, textAlign: 'center' }}>
                  Loading reports…
                </div>
              ) : files.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 60 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
                  No reports uploaded yet.<br />
                  <span style={{ fontSize: 12 }}>Upload lab reports or scans from the <strong>Files &amp; Docs</strong> tab.</span>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
                    {files.length} report{files.length !== 1 ? 's' : ''} — click a card to view details
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    gap: 14,
                  }}>
                    {files.map(f => (
                      <ReportCard
                        key={f.filename}
                        file={f}
                        patientCard={patientCard}
                        onClick={f => setSelected(f)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
